// NEW_FILE_JUSTIFIED: Lead detail panel wrapping AiDraftBubble with status pill + regenerate button
import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is the official client-side React bindings package, not a server module
import { Sparkles, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { api } from '@convex/_generated/api';
import type { IAiReplyDraft, TAiDraftStatus } from '@/@types/aiReply';
import type { ILeadAiDraftPanelProps } from '@/@types/leads';
import { AiDraftBubble } from '@/components/aiReply/AiDraftBubble';
import { useTriggerWebsiteLeadAi } from '@/hooks/useTriggerWebsiteLeadAi';

const STATUS_LABEL: Record<TAiDraftStatus, string> = {
  pending: 'Pending review',
  sent: 'Sent',
  edited_and_sent: 'Edited & sent',
  cancelled: 'Cancelled',
  superseded: 'Superseded',
};

const STATUS_TONE: Record<TAiDraftStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-700',
  sent: 'bg-emerald-500/10 text-emerald-700',
  edited_and_sent: 'bg-emerald-500/10 text-emerald-700',
  cancelled: 'bg-muted text-muted-foreground',
  superseded: 'bg-muted text-muted-foreground',
};

const DRAFT_STATUSES = ['pending', 'sent', 'edited_and_sent', 'cancelled', 'superseded'] as const;

function isDraftStatus(value: unknown): value is TAiDraftStatus {
  return typeof value === 'string' && (DRAFT_STATUSES as readonly string[]).includes(value);
}

function isDraft(row: unknown): row is IAiReplyDraft {
  return (
    typeof row === 'object' && row !== null && '_id' in row && 'draftText' in row && 'status' in row
  );
}

export function LeadAiDraftPanel({ leadId }: ILeadAiDraftPanelProps) {
  const drafts = useQuery(api.aiReply.draftsPublic.listByLead, { leadId });
  const triggerAi = useTriggerWebsiteLeadAi();
  const markSent = useMutation(api.aiReply.drafts.markSent);
  const cancelDraft = useMutation(api.aiReply.drafts.cancel);
  const [regenerating, setRegenerating] = useState(false);

  if (!drafts || drafts.length === 0) return null;

  const first = drafts[0];
  if (!isDraft(first)) return null;

  const status = isDraftStatus(first.status) ? first.status : 'pending';

  const handleSend = async (draftId: IAiReplyDraft['_id'], text: string) => {
    await markSent({ draftId, editedText: text !== first.draftText ? text : undefined });
  };

  const handleCancel = async (draftId: IAiReplyDraft['_id']) => {
    await cancelDraft({ draftId });
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await triggerAi(leadId);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-primary" />
          AI draft reply
          <Badge className={cn('ml-2', STATUS_TONE[status])}>{STATUS_LABEL[status]}</Badge>
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          disabled={regenerating}
          onClick={handleRegenerate}
          aria-label="Regenerate AI draft"
        >
          <RefreshCw className={cn('h-4 w-4', regenerating && 'animate-spin')} />
          <span className="ml-2">Regenerate</span>
        </Button>
      </CardHeader>
      <CardContent>
        <AiDraftBubble draft={first} onSend={handleSend} onCancel={handleCancel} />
      </CardContent>
    </Card>
  );
}
